import { byPosition } from './position.js';
import { calendarDate, dueDeadline, localDateKey } from './dates.js';
import { PRIORITIES, type CardSummary } from './types.js';

/* ---------- Typen ---------- */

export type DueFilter = 'overdue' | 'today' | 'week' | 'later' | 'none';
export const DUE_FILTERS: DueFilter[] = ['overdue', 'today', 'week', 'later', 'none'];
export const DUE_FILTER_LABEL: Record<DueFilter, string> = {
  overdue: 'Überfällig',
  today: 'Heute',
  week: 'Nächste 7 Tage',
  later: 'Später',
  none: 'Ohne Frist',
};

/** Innerhalb einer Gruppe gilt „oder“, zwischen Gruppen „und“ */
export interface BoardFilter {
  q?: string;
  /** Personen-IDs, dazu 'me' (ich) und 'none' (niemand zuständig) */
  assignees?: string[];
  /** Label-IDs, dazu 'none' (ohne Label) */
  labels?: string[];
  due?: DueFilter[];
  /** 0 = keine … 4 = dringend */
  priority?: number[];
  /** nur blockierte Karten */
  blocked?: boolean;
  /** Erledigte: zeigen (Standard), ausblenden oder nur diese */
  completed?: 'hide' | 'only';
}

export type CardSort = 'manual' | 'due' | 'priority' | 'updated' | 'title';
export const CARD_SORT_LABEL: Record<CardSort, string> = {
  manual: 'Manuell (Ziehen)',
  due: 'Frist',
  priority: 'Priorität',
  updated: 'Zuletzt geändert',
  title: 'Titel A–Z',
};

export type Density = 'compact' | 'standard' | 'visual';
export const DENSITY_LABEL: Record<Density, string> = {
  compact: 'Kompakt',
  standard: 'Standard',
  visual: 'Visuell',
};

export type BoardView = 'board' | 'list' | 'table' | 'calendar' | 'timeline' | 'metrics';

/** Zeilen (Swimlanes) im Board – aus, solange nicht gewählt */
export type LaneMode = 'owner' | 'priority' | 'label';
export const LANE_LABEL: Record<LaneMode, string> = {
  owner: 'Hauptverantwortlich',
  priority: 'Priorität',
  label: 'Label',
};

export interface ViewConfig {
  view?: BoardView;
  filter: BoardFilter;
  sort: CardSort;
  density: Density;
  /** Zeilen; fehlt = keine Zeilen */
  lanes?: LaneMode;
  /** Auch Zeilen ohne Karten zeigen (z. B. um Personen per Ziehen zuzuweisen) */
  emptyLanes?: boolean;
}

export interface SavedView {
  id: string;
  boardId: string;
  name: string;
  /** true = für alle im Board sichtbar; sonst nur für die Person, die sie angelegt hat */
  shared: boolean;
  ownerId: string | null;
  config: ViewConfig;
  position: string;
}

export const DEFAULT_VIEW_CONFIG: ViewConfig = { filter: {}, sort: 'manual', density: 'standard' };

/* ---------- Filter ---------- */

export interface FilterContext {
  meId?: string;
  /** Label-ID → Name (für die Textsuche) */
  labelNames?: Map<string, string>;
  now?: Date;
}

export function isFilterActive(f: BoardFilter): boolean {
  return !!(
    f.q?.trim() ||
    f.assignees?.length ||
    f.labels?.length ||
    f.due?.length ||
    f.priority?.length ||
    f.blocked ||
    f.completed
  );
}

/** Anzahl aktiver Filtergruppen (für die Anzeige am Filterknopf) */
export function activeFilterCount(f: BoardFilter): number {
  return [f.q?.trim(), f.assignees?.length, f.labels?.length, f.due?.length, f.priority?.length, f.blocked, f.completed].filter(Boolean)
    .length;
}

export function dueBucket(card: Pick<CardSummary, 'dueAt' | 'dueHasTime'>, now = new Date()): DueFilter {
  if (!card.dueAt) return 'none';
  const deadline = dueDeadline(card.dueAt, card.dueHasTime);
  if (deadline.getTime() < now.getTime()) return 'overdue';
  const day = calendarDate(card.dueAt, card.dueHasTime);
  const today = localDateKey(now);
  if (day === today) return 'today';
  const inAWeek = localDateKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7));
  return day <= inAWeek ? 'week' : 'later';
}

export function matchesFilter(card: CardSummary, f: BoardFilter, ctx: FilterContext = {}): boolean {
  if (f.completed === 'hide' && card.completedAt) return false;
  if (f.completed === 'only' && !card.completedAt) return false;
  if (f.blocked && !card.blocked) return false;
  if (f.assignees?.length) {
    const ok = f.assignees.some((a) =>
      a === 'none' ? card.assigneeIds.length === 0 : card.assigneeIds.includes(a === 'me' ? (ctx.meId ?? '') : a),
    );
    if (!ok) return false;
  }
  if (f.labels?.length) {
    const ok = f.labels.some((l) => (l === 'none' ? card.labelIds.length === 0 : card.labelIds.includes(l)));
    if (!ok) return false;
  }
  if (f.priority?.length && !f.priority.includes(card.priority)) return false;
  if (f.due?.length) {
    // Erledigte Karten sind nie „überfällig“
    const bucket = dueBucket(card, ctx.now);
    const b = card.completedAt && bucket === 'overdue' ? 'later' : bucket;
    if (!f.due.includes(b)) return false;
  }
  const q = f.q?.trim().toLowerCase();
  if (q) {
    const hay = [card.title, ...card.labelIds.map((id) => ctx.labelNames?.get(id) ?? '')].join(' ').toLowerCase();
    // Alle Wörter müssen vorkommen (Reihenfolge egal)
    if (!q.split(/\s+/).every((w) => hay.includes(w))) return false;
  }
  return true;
}

/* ---------- Sortierung ---------- */

const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

export function cardComparator(sort: CardSort): (a: CardSummary, b: CardSummary) => number {
  switch (sort) {
    case 'manual':
      return byPosition;
    case 'due':
      return (a, b) => {
        const da = a.dueAt ? dueDeadline(a.dueAt, a.dueHasTime).getTime() : Infinity;
        const db = b.dueAt ? dueDeadline(b.dueAt, b.dueHasTime).getTime() : Infinity;
        return da === db ? byPosition(a, b) : da < db ? -1 : 1;
      };
    case 'priority':
      return (a, b) => b.priority - a.priority || byPosition(a, b);
    case 'updated':
      return (a, b) => (a.updatedAt === b.updatedAt ? byPosition(a, b) : a.updatedAt < b.updatedAt ? 1 : -1);
    case 'title':
      return (a, b) => collator.compare(a.title, b.title) || byPosition(a, b);
  }
}

/* ---------- URL ---------- */

/**
 * Ansicht ⇄ URL-Parameter (lesbar, damit Links teilbar sind):
 * q, who, label, due, prio, blocked=1, done=hide|only, sort, dens, lanes, elanes=1
 */
export function configToParams(c: ViewConfig): Record<string, string | null> {
  const f = c.filter;
  const list = (x?: (string | number)[]) => (x?.length ? x.join(',') : null);
  return {
    q: f.q?.trim() || null,
    who: list(f.assignees),
    label: list(f.labels),
    due: list(f.due),
    prio: list(f.priority),
    blocked: f.blocked ? '1' : null,
    done: f.completed ?? null,
    sort: c.sort !== 'manual' ? c.sort : null,
    dens: c.density !== 'standard' ? c.density : null,
    lanes: c.lanes ?? null,
    elanes: c.lanes && c.emptyLanes ? '1' : null,
  };
}

export function configFromParams(p: URLSearchParams, fallback: Pick<ViewConfig, 'density'> = { density: 'standard' }): ViewConfig {
  const list = (k: string) => (p.get(k) ? p.get(k)!.split(',').filter(Boolean) : undefined);
  const sort = p.get('sort') as CardSort | null;
  const dens = p.get('dens') as Density | null;
  const done = p.get('done');
  const filter: BoardFilter = {};
  if (p.get('q')) filter.q = p.get('q')!;
  if (list('who')) filter.assignees = list('who');
  if (list('label')) filter.labels = list('label');
  const due = list('due')?.filter((d): d is DueFilter => (DUE_FILTERS as string[]).includes(d));
  if (due?.length) filter.due = due;
  const prio = list('prio')
    ?.map(Number)
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 4);
  if (prio?.length) filter.priority = prio;
  if (p.get('blocked') === '1') filter.blocked = true;
  if (done === 'hide' || done === 'only') filter.completed = done;
  const lanes = p.get('lanes') as LaneMode | null;
  const out: ViewConfig = {
    filter,
    sort: sort && sort in CARD_SORT_LABEL ? sort : 'manual',
    density: dens && dens in DENSITY_LABEL ? dens : fallback.density,
  };
  if (lanes && lanes in LANE_LABEL) {
    out.lanes = lanes;
    if (p.get('elanes') === '1') out.emptyLanes = true;
  }
  return out;
}

/** Gleicher Inhalt? (für „gespeicherte Ansicht ist aktiv“) */
export function sameConfig(a: ViewConfig, b: ViewConfig): boolean {
  const pa = configToParams(a);
  const pb = configToParams(b);
  return Object.keys({ ...pa, ...pb }).every((k) => (pa[k] ?? null) === (pb[k] ?? null)) && (a.view ?? null) === (b.view ?? null);
}

/* ---------- Aktive Filter als Chips ---------- */

export interface FilterChip {
  /** stabil, für React-Keys und Tests */
  key: string;
  /** sichtbarer Text, z. B. „Zuständig: Ich“ */
  label: string;
  /** Ansicht ohne diesen einen Filter */
  without: ViewConfig;
}

/**
 * Alles, was die Kartenmenge oder ihre Reihenfolge gerade verändert, als einzeln entfernbare Chips.
 * Die Kartengröße zählt nicht dazu (persönliche Geräteeinstellung).
 */
export function filterChips(
  config: ViewConfig,
  names: { member: (id: string) => string | undefined; label: (id: string) => string | undefined },
): FilterChip[] {
  const f = config.filter;
  const out: FilterChip[] = [];
  const withFilter = (patch: Partial<BoardFilter>): ViewConfig => {
    const filter: BoardFilter = { ...f, ...patch };
    for (const k of Object.keys(filter) as (keyof BoardFilter)[]) {
      const v = filter[k];
      if (v === undefined || (Array.isArray(v) && !v.length)) delete filter[k];
    }
    return { ...config, filter };
  };
  const drop = <T>(list: T[] | undefined, v: T) => list?.filter((x) => x !== v);

  if (f.q?.trim()) out.push({ key: 'q', label: `Suche: „${f.q.trim()}“`, without: withFilter({ q: undefined }) });
  for (const a of f.assignees ?? [])
    out.push({
      key: `a:${a}`,
      label: `Zuständig: ${a === 'me' ? 'Ich' : a === 'none' ? 'Niemand' : (names.member(a) ?? 'Ehemaliges Mitglied')}`,
      without: withFilter({ assignees: drop(f.assignees, a) }),
    });
  for (const l of f.labels ?? [])
    out.push({
      key: `l:${l}`,
      label: l === 'none' ? 'Ohne Label' : `Label: ${names.label(l) || 'ohne Namen'}`,
      without: withFilter({ labels: drop(f.labels, l) }),
    });
  for (const d of f.due ?? []) out.push({ key: `d:${d}`, label: `Frist: ${DUE_FILTER_LABEL[d]}`, without: withFilter({ due: drop(f.due, d) }) });
  for (const p of f.priority ?? [])
    out.push({
      key: `p:${p}`,
      label: `Priorität: ${PRIORITIES.find((x) => x.value === p)?.label ?? p}`,
      without: withFilter({ priority: drop(f.priority, p) }),
    });
  if (f.blocked) out.push({ key: 'blocked', label: 'Nur blockierte', without: withFilter({ blocked: undefined }) });
  if (f.completed)
    out.push({
      key: 'completed',
      label: f.completed === 'hide' ? 'Erledigte ausgeblendet' : 'Nur erledigte',
      without: withFilter({ completed: undefined }),
    });
  if (config.sort !== 'manual') out.push({ key: 'sort', label: `Sortiert: ${CARD_SORT_LABEL[config.sort]}`, without: { ...config, sort: 'manual' } });
  if (config.lanes) {
    const without: ViewConfig = { ...config };
    delete without.lanes;
    delete without.emptyLanes;
    out.push({ key: 'lanes', label: `Zeilen: ${LANE_LABEL[config.lanes]}`, without });
  }
  return out;
}
